import { useQuery } from "@apollo/client";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import { AppContext } from "../context";
import { IComingState } from "../Pages/agreements/interfaces";
import { FETCH_CURRENT_USER } from "../Pages/settings/gql";
import { LIST_ROUTES } from "../utils/constant";
import { destroyRouteHistory } from "../utils/helper";
import MainLayout from "./layout/MainLayout";
import { ALL_MODAL_ACCCESS_SETTINGS } from "../Pages/moduleAccess-settings/gql";
import { checkUserFeatureAccess } from "../utils/commonFn";

const PrivateRoute = ({ component: Component, roles, path, ...rest }: any) => {
  const token = window.localStorage.getItem("token");
  const { data, loading: userLoading, error: userError, refetch: refetchUser } =
    useQuery(FETCH_CURRENT_USER);
  const { data: settingsData, loading } = useQuery(ALL_MODAL_ACCCESS_SETTINGS);
  const { setContextRoles, setContextUser } = useContext(AppContext);
  const location = useLocation<IComingState>()
  const retriedMeRef = useRef(false);

  // A settled `me` with NO user means the session can't identify itself —
  // without this effect the app renders every page as an empty shell (blank
  // Settings form, default avatar, role-gated links hidden) while the token
  // silently fails. That state used to occur when an expired token's silent
  // refresh hit a transient failure (the error-link's "preserve" outcome
  // keeps the session but lets the in-flight queries die). Retry once for
  // genuine blips; if it still can't identify the user and the failure is
  // auth-shaped, clear the session and return to login. Pure network errors
  // (offline) never force a logout, and impersonation sessions are left to
  // the error-link's restore flow.
  //
  // Precedence when the retry fails for a DIFFERENT reason than the original
  // error: BOTH errors feed the auth-shaped check, so an auth-failed original
  // whose retry dies on a network blip still signs out. Deliberate — by then
  // the error-link's refresh has had two full chances, and keeping an
  // auth-broken session alive is the blank-shell bug this guard exists to fix.
  useEffect(() => {
    if (userLoading || data?.me?.user || retriedMeRef.current) {
      return;
    }
    retriedMeRef.current = true;
    const signOutIfAuthFailure = (errors: any[]) => {
      const messages = errors
        .map((err: any) => err?.message ?? '')
        .join(' ')
        .toLowerCase();
      const authShaped = /jwt|expired|token|unauthorized|forbidden|invalid signature/.test(messages);
      const isImpersonationSession = !!localStorage.getItem('previousToken');
      if (authShaped && !isImpersonationSession) {
        ['token', 'refreshToken', 'previousToken', 'previousRefreshToken', 'previousSideBarItem']
          .forEach((key) => localStorage.removeItem(key));
        window.location.href = '/';
      }
    };
    // The decision runs inside the retry's own promise chain — a refetch that
    // fails with the same error may not re-render the hook, so an effect
    // re-run can't be relied on here.
    const timer = setTimeout(() => {
      refetchUser()
        .then((result: any) => {
          if (result?.data?.me?.user) {
            return; // transient blip — recovered
          }
          signOutIfAuthFailure([
            ...(result?.errors ?? []),
            ...(userError?.graphQLErrors ?? []),
            userError,
          ].filter(Boolean));
        })
        .catch((err: any) => {
          signOutIfAuthFailure([
            err,
            ...(err?.graphQLErrors ?? []),
            ...(userError?.graphQLErrors ?? []),
            userError,
          ].filter(Boolean));
        });
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading, data, userError]);

  const mountEffect = () => {
    setContextUser(data?.me?.user || null);
    const userRole = data?.me?.user?.userRoles;
    if (userRole && userRole.length) {
      const _roles = userRole.map((role: any) => role.role.name);
      setContextRoles(_roles || []);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(mountEffect, [data]);

  // destroying routes history on visiting list
  useEffect(() => {
    if (LIST_ROUTES.includes(location.pathname)) {
      destroyRouteHistory()
    }
  }, [location.pathname])

  return (
    // Show the component only when the user is logged in
    // Otherwise, redirect the user to /signin page

    <Route
      {...rest}
      render={(props) => {
        if (!token) {
          return <Redirect to="/" />;
        } else {
          if (path === '/job-list' && !loading && !userLoading &&
            !checkUserFeatureAccess(data?.me?.user?.id, 'JOB_LIST', settingsData)) {
            return <Redirect to="/un-authorized" />;
          }
          if (data?.me?.user?.userRoles?.length) {
            if (!roles) {
              return (
                <>
                  <MainLayout>
                    <Component {...props} />
                  </MainLayout>
                </>
              );
            }
            // eslint-disable-next-line
            const _userRoles = data?.me?.user.userRoles.map(
              (item: any) => item.role.name
            );
            // eslint-disable-next-line
            const hasAccess = _userRoles?.find((item: any) => {
              if (roles?.includes(item)) {
                return item;
              }
            });

            if (hasAccess) {
              return (
                <>
                  <MainLayout>
                    <Component {...props} />
                  </MainLayout>
                </>
              );
            } else {
              return <Redirect to="/un-authorized" />;
            }
          } else {
            return (
              <MainLayout>
                <Component {...props} />
              </MainLayout>
            );
          }
        }
      }}
    />
  );
};

export default PrivateRoute;
